import { __awaiter } from "tslib";
/// <reference types="dom-speech-recognition" />
import { useEffect, useRef, useState } from 'react';
import { useLatestRef, usePersistentCallback } from '../hooks';
import { useAudioPlayer } from './useAudioPlayer';
import { useSilenceDetection } from './useSilenceDetection';
export const useBrowserVoiceRecognition = ({ silenceThreshold = 0.1, silenceTimeoutDuration = 1500, onPredict, recordingStartSound } = {}) => {
    const [isRecording, setIsRecording] = useState(false);
    const [predictedTranscript, setPredictedTranscript] = useState('');
    const [error, setError] = useState(null);
    const [audio, setAudio] = useState(null);
    const recognitionRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserNodeRef = useRef(null);
    const streamRef = useRef(null);
    const { play } = useAudioPlayer();
    const predictedTranscriptRef = useLatestRef(predictedTranscript);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const recordingStartTimeRef = useRef(null);
    const recordingIntervalRef = useRef(null);
    const recordingDurationRef = useLatestRef(recordingDuration);
    const reset = usePersistentCallback(() => {
        setPredictedTranscript('');
        setError(null);
        setAudio(null);
        setRecordingDuration(0); // Reset duration when starting a new recording
    });
    const stopRecording = usePersistentCallback(() => {
        if (mediaRecorderRef.current)
            mediaRecorderRef.current.stop();
        if (recognitionRef.current)
            recognitionRef.current.stop();
        if (analyserNodeRef.current)
            analyserNodeRef.current.disconnect();
        if (audioContextRef.current)
            audioContextRef.current.close();
        if (streamRef.current)
            streamRef.current.getTracks().forEach((track) => track.stop());
        stopSilenceDetection();
        // stop recording duration timer
        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
        }
        recordingStartTimeRef.current = null;
    });
    const { detectSilence, stopSilenceDetection } = useSilenceDetection(silenceThreshold, silenceTimeoutDuration, stopRecording);
    const startRecording = usePersistentCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        reset();
        try {
            if (recordingStartSound)
                yield play(recordingStartSound);
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            // Ask for microphone access
            const stream = yield navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            const chunks = [];
            // Handle recording data (collect audio chunks)
            mediaRecorder.ondataavailable = (event) => {
                chunks.push(event.data);
            };
            mediaRecorder.onstop = () => {
                const blob = new Blob(chunks, { type: 'audio/ogg; codecs=opus' });
                setAudio(blob); // save the recorded audio as a blob
                setIsRecording(false);
                onPredict === null || onPredict === void 0 ? void 0 : onPredict({
                    transcript: predictedTranscriptRef.current,
                    audio: blob,
                    recordingDuration: recordingDurationRef.current
                });
            };
            // start recording
            mediaRecorder.start();
            setIsRecording(true);
            // if Speech Recognition is supported, use it for live transcription
            if (SpeechRecognition) {
                const recognition = new SpeechRecognition();
                recognition.continuous = true;
                recognition.interimResults = true;
                recognition.lang = 'en-US';
                recognitionRef.current = recognition;
                // Handle transcription updates
                recognition.onresult = (event) => {
                    let transcript = '';
                    for (let i = event.resultIndex; i < event.results.length; i++) {
                        transcript += event.results[i][0].transcript;
                    }
                    setPredictedTranscript(transcript);
                };
                recognition.onerror = (event) => {
                    // in Google Chrome the transcription is done by sending the voice to google servers
                    // in chromium based browsers the google servers are not accessible we get network error
                    // so we ignore that error and say that we are transcribing
                    if (event.error === 'network') {
                        setPredictedTranscript('Transcribing...');
                    }
                    else {
                        setError(`Failed to recognise your voice: ${event.error}`);
                    }
                };
                // start speech recognition
                recognition.start();
            }
            // Setup Web Audio API for visualization and silence detection
            const audioContext = new window.AudioContext();
            audioContextRef.current = audioContext;
            const analyserNode = audioContext.createAnalyser();
            analyserNode.fftSize = 256; // Smaller FFT size for a smoother bar effect
            analyserNodeRef.current = analyserNode;
            const sourceNode = audioContext.createMediaStreamSource(stream);
            sourceNode.connect(analyserNode);
            detectSilence(analyserNode);
            // Start recording duration timer
            recordingStartTimeRef.current = Date.now();
            recordingIntervalRef.current = setInterval(() => {
                if (recordingStartTimeRef.current) {
                    const duration = Date.now() - recordingStartTimeRef.current;
                    setRecordingDuration(duration);
                }
            }, 100);
        }
        catch (err) {
            const _err = err;
            if (_err.name === 'NotAllowedError' || _err.message.includes('Permission denied')) {
                setError('Microphone permission denied. Please allow microphone access and try again.');
            }
            else {
                setError(`Failed to record your voice: ${_err.message}`);
            }
        }
    }));
    // cleanup any audio or recognition resources when the component unmounts
    useEffect(() => {
        return () => stopRecording();
    }, [stopRecording]);
    return {
        isRecording,
        analyserNodeRef,
        audio,
        error,
        startRecording,
        stopRecording,
        predictedTranscript,
        reset,
        recordingDuration
    };
};
